import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { FaCar, FaBicycle, FaWalking, FaInfoCircle, FaRoute, FaClock, FaArrowRight, FaExpandArrowsAlt, FaCompressArrowsAlt, FaChevronDown, FaMapMarkerAlt } from 'react-icons/fa';
import Header from './Header';
import About from './About';
import Contact from './Contact';
import Footer from './Footer';
import AdBanner from './AdBanner';
import Attribution from './Attribution';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function App() {
  const [addresses, setAddresses] = useState('');
  const [routeData, setRouteData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedLegs, setExpandedLegs] = useState(new Set());
  const [showOpenInDropdown, setShowOpenInDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const addressInputInfoRef = useRef(null);
  const distanceInfoRef = useRef(null);
  const copyDropdownRef = useRef(null);
  const [showCopyDropdown, setShowCopyDropdown] = useState(false);
  const [transportationType, setTransportationType] = useState('Car');
  const [region, setRegion] = useState('Europe'); // Default region
  const [distanceUnit, setDistanceUnit] = useState('km');
  const [showAddressInputInfo, setShowAddressInputInfo] = useState(false);
  const [showDistanceInfo, setShowDistanceInfo] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [config, setConfig] = useState({
    max_addresses: 20,
    max_address_length: 250,
    supported_options: {
      regions: [], // Initialize as empty arrays
      transportation_types: [], // Initialize as empty arrays
    },
  });
  const [isConfigLoaded, setIsConfigLoaded] = useState(false); // New state for config loading

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/config`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
          if (data.supported_options.regions && data.supported_options.regions.length > 0) {
            const europeRegion = data.supported_options.regions.find(r => r.key === 'Europe');
            if (europeRegion) {
              setRegion('Europe');
            } else {
              setRegion(data.supported_options.regions[0].key);
            }
          }
          setIsConfigLoaded(true); // Set to true after config is loaded
        } else {
          console.error('Failed to fetch configuration');
        }
      } catch (error) {
        console.error('Error fetching configuration:', error);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config.supported_options.regions.length > 0 && region) {
      const currentRegionConfig = config.supported_options.regions.find(r => r.key === region);
      if (currentRegionConfig && currentRegionConfig.active_transportation_types.length > 0) {
        const isCurrentTypeActive = currentRegionConfig.active_transportation_types.some(activeType => activeType.key === transportationType);
        if (!isCurrentTypeActive) {
          // If current transportation type is not active for the selected region, default to the first active type
          setTransportationType(currentRegionConfig.active_transportation_types[0].key);
        }
      }
    }
  }, [region, config, transportationType]);

  const copyToClipboard = (text, successMessage) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(successMessage);
      setTimeout(() => setCopySuccess(''), 2000); // Clear message after 2 seconds
    }).catch(err => {
      setCopySuccess('Failed to copy.');
      console.error('Could not copy text: ', err);
    });
  };

  const handleCopyOriginal = () => {
    if (addresses) {
      copyToClipboard(addresses, 'Original addresses copied!');
    }
  };

  const handleCopyResolved = () => {
    if (routeData && routeData.optimized_route && routeData.optimized_route.length > 0) {
      const resolvedAddresses = routeData.optimized_route.map(stop => stop.fullAddress).join('\n');
      copyToClipboard(resolvedAddresses, 'Resolved addresses copied!');
    }
  };

  const handleCopyCsv = () => {
    if (routeData && routeData.optimized_route && routeData.legs && routeData.optimized_route.length > 0) {
      let csvContent = "Original Address,Resolved Address,Latitude,Longitude,Leg Distance (km),Leg Duration (seconds)\n";

      routeData.optimized_route.forEach((stop, index) => {
        const originalAddress = stop.address;
        const resolvedAddress = stop.fullAddress;
        const lat = stop.lat;
        const lon = stop.lon;

        let legDistance = '';
        let legDuration = '';

        if (index < routeData.legs.length) {
          legDistance = (routeData.legs[index].distance / 1000).toFixed(2); // Convert meters to km
          legDuration = routeData.legs[index].duration.toFixed(0); // Seconds
        }

        csvContent += `"${originalAddress}","${resolvedAddress}",${lat},${lon},${legDistance},${legDuration}\n`;
      });
      copyToClipboard(csvContent, 'Route data (CSV) copied!');
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOpenInDropdown(false);
      }
      if (addressInputInfoRef.current && !addressInputInfoRef.current.contains(event.target)) {
        setShowAddressInputInfo(false);
      }
      if (distanceInfoRef.current && !distanceInfoRef.current.contains(event.target)) {
        setShowDistanceInfo(false);
      }
      if (copyDropdownRef.current && !copyDropdownRef.current.contains(event.target)) {
        setShowCopyDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, addressInputInfoRef, distanceInfoRef, copyDropdownRef, setShowOpenInDropdown, setShowAddressInputInfo, setShowDistanceInfo, setShowCopyDropdown]);

  const renderAd = (position) => {
    if (!config.ads || !config.ads.enabled || !config.ads.adsense) {
      return null;
    }

    const adUnit = config.ads.adsense.ad_units.find(unit => unit.enabled && unit.position === position);

    if (!adUnit) {
      return null;
    }

    return (
      <AdBanner
        key={adUnit.id}
        clientId={config.ads.adsense.client_id}
        slotId={adUnit.slot_id}
        position={adUnit.position}
        visualizationMode={config.ads.visualization_mode}
      />
    );
  };

  const optimizerProps = { addresses, setAddresses, routeData, setRouteData, error, setError, isLoading, setIsLoading, expandedLegs, setExpandedLegs, showOpenInDropdown, setShowOpenInDropdown, dropdownRef, copyDropdownRef, showCopyDropdown, setShowCopyDropdown, addressInputInfoRef, distanceInfoRef, transportationType, setTransportationType, distanceUnit, setDistanceUnit, showAddressInputInfo, setShowAddressInputInfo, showDistanceInfo, setShowDistanceInfo, copySuccess, setCopySuccess, handleCopyOriginal, handleCopyResolved, handleCopyCsv, region, setRegion, config, renderAd };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="app-container">
        <Header />
        {renderAd('header')}
        <Routes>
          <Route path="/" element={
            isConfigLoaded ? (
              <div className="main-content-area">
                {renderAd('left')}
                <div className="optimizer-main-panel">
                  <Optimizer {...optimizerProps} />
                </div>
                {renderAd('right')}
              </div>
            ) : (
              <div>Loading configuration...</div> // Or a spinner
            )
          } />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/attribution" element={<Attribution />} />
        </Routes>
        {renderAd('footer')}
        <Footer />
      </div>
    </Router>
  );
}

function Optimizer({ addresses, setAddresses, routeData, setRouteData, error, setError, isLoading, setIsLoading, expandedLegs, setExpandedLegs, showOpenInDropdown, setShowOpenInDropdown, dropdownRef, copyDropdownRef, showCopyDropdown, setShowCopyDropdown, addressInputInfoRef, distanceInfoRef, transportationType, setTransportationType, distanceUnit, setDistanceUnit, showAddressInputInfo, setShowAddressInputInfo, showDistanceInfo, setShowDistanceInfo, copySuccess, setCopySuccess, handleCopyOriginal, handleCopyResolved, handleCopyCsv, region, setRegion, config, renderAd }) {

  // Helper to determine if locale uses miles and convert
  const getLocalizedDistance = (distanceKm) => {
    const locale = navigator.language;

    let value;
    let unit;

    if (distanceUnit === 'miles') {
      value = distanceKm * 0.621371; // Convert km to miles
      unit = 'mile';
    } else {
      value = distanceKm;
      unit = 'kilometer';
    }

    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: unit,
      unitDisplay: 'long', // 'short', 'narrow'
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Helper to format duration into hours and minutes using Intl.DurationFormat or fallback
  const formatDuration = (durationSeconds) => {
    const totalMinutes = Math.round(durationSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const locale = navigator.language;

    // Try to use Intl.DurationFormat if available
    if (typeof Intl.DurationFormat !== 'undefined') {
      const durationParts = {};
      if (hours > 0) durationParts.hours = hours;
      if (minutes > 0) durationParts.minutes = minutes;
      if (hours === 0 && minutes === 0) durationParts.minutes = 0; // Ensure "0 minutes" is shown

      return new Intl.DurationFormat(locale, {
        style: 'long', // 'short', 'narrow'
      }).format(durationParts);
    } else {
      // Fallback for browsers that don't support Intl.DurationFormat
      const formattedHours = new Intl.NumberFormat(locale).format(hours);
      const formattedMinutes = new Intl.NumberFormat(locale).format(minutes);

      let result = '';
      if (hours > 0) {
        result += `${formattedHours} ${hours === 1 ? 'hour' : 'hours'}`;
      }
      if (minutes > 0) {
        if (result !== '') result += ' ';
        result += `${formattedMinutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
      }
      if (result === '' && totalMinutes === 0) {
        result = `0 ${minutes === 1 ? 'minute' : 'minutes'}`;
      }
      return result;
    }
  };

  const handleToggleLeg = (index) => {
    setExpandedLegs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    const allIndices = new Set(routeData.legs.map((_, i) => i));
    setExpandedLegs(allIndices);
  };

  const handleCollapseAll = () => {
    setExpandedLegs(new Set());
  };

  const handleOptimizeRoute = async () => {
    setError(null);
    setRouteData(null);
    setIsLoading(true);

    const addressList = addresses.split('\n').map(addr => addr.trim()).filter(addr => addr.length > 0);

    if (addressList.length < 2 || addressList.length > config.max_addresses) {
      setError(`Please enter between 2 and ${config.max_addresses} addresses.`);
      setIsLoading(false);
      return;
    }

    for (const address of addressList) {
      if (address.length === 0 || address.length > config.max_address_length) {
        setError(`Each address must be between 1 and ${config.max_address_length} characters long.`);
        setIsLoading(false);
        return;
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/optimize-route`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ addresses: addressList, transportationType, region }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'An unknown error occurred.');
        setIsLoading(false);
        return;
      }

      pollForResult(data.jobId);

    } catch (err) {
      setError('Failed to connect to the backend server. Make sure it is running.');
      console.error(err);
      setIsLoading(false);
    }
  };

  const pollForResult = async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/optimize-route/result/${jobId}`);

        if (!response.ok) {
          // If the job is not found, it might have been cleared after a successful poll.
          // We can stop polling without showing an error.
          if (response.status === 404) {
            console.log("Job not found, assuming it was completed and cleared.");
            clearInterval(pollInterval);
            return;
          }
          // For other errors, show a message.
          const errorData = await response.json();
          setError(errorData.error || 'An error occurred while fetching the result.');
          setIsLoading(false);
          clearInterval(pollInterval);
          return;
        }

        const data = await response.json();

        if (data.status === 'completed') {
          setRouteData(data.result);
          setIsLoading(false);
          clearInterval(pollInterval);
        } else if (data.status === 'failed') {
          setError(data.error || 'An unknown error occurred.');
          setIsLoading(false);
          clearInterval(pollInterval);
        }
        // If status is still 'pending', the loop will continue.

      } catch (err) {
        setError('Failed to connect to the backend server. Make sure it is running.');
        console.error(err);
        setIsLoading(false);
        clearInterval(pollInterval);
      }
    }, 2000);
  };

  const handleClear = () => {
    setAddresses('');
    setRouteData(null);
    setError(null);
  };

  const openGoogleMaps = () => {
    if (routeData && routeData.optimized_route && routeData.optimized_route.length > 0) {
      // Using the full resolved address is more accurate for external services
      const addressesForMaps = routeData.optimized_route.map(stop => encodeURIComponent(stop.fullAddress)).join('/');
      const googleMapsUrl = `https://www.google.com/maps/dir/${addressesForMaps}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  const openGraphHopper = () => {
    if (routeData && routeData.optimized_route && routeData.optimized_route.length > 0) {
      const points = routeData.optimized_route.map(stop => `point=${stop.lat},${stop.lon}`).join('&');
      let profile = 'car';
      if (transportationType === 'Bicycle') profile = 'bike';
      if (transportationType === 'Walking') profile = 'foot';

      const graphHopperUrl = `https://graphhopper.com/maps/?${points}&profile=${profile}`;
      window.open(graphHopperUrl, '_blank');
    }
  };

  const openABetterRouteplanner = () => {
    if (routeData && routeData.optimized_route && routeData.optimized_route.length > 0) {
      const destinations = routeData.optimized_route.map(stop => ({
        address: stop.fullAddress, // Use the full resolved address
        lat: stop.lat,
        lon: stop.lon,
      }));
      const encodedDestinations = encodeURIComponent(JSON.stringify(destinations));
      const abrpUrl = `https://abetterrouteplanner.com/?destinations=${encodedDestinations}`;
      window.open(abrpUrl, '_blank');
    }
  };

  const openBingMaps = () => {
    if (routeData && routeData.optimized_route && routeData.optimized_route.length > 0) {
      const waypoints = routeData.optimized_route.map(stop => `pos.${stop.lat}_${stop.lon}`).join('~');
      const bingMapsUrl = `https://bing.com/maps/default.aspx?rtp=${waypoints}`;
      window.open(bingMapsUrl, '_blank');
    }
  };

  return (
    <div className="optimizer-container">
      <div className="input-panel">
        <h3 className="panel-header">
          Enter Your Stops
          {!(config.supported_options.regions.length === 1 && config.supported_options.regions[0].key === 'Planet') && (
            <div className="region-selector">
              <select id="region-select" value={region} onChange={(e) => setRegion(e.target.value)}>
                {config.supported_options.regions.map((r) => (
                  <option key={r.key} value={r.key}>{r.display_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="transport-options-header">
            {config.supported_options.transportation_types
              .filter(type => 
                config.supported_options.regions.some(regionConfig => 
                  regionConfig.active_transportation_types.some(activeType => activeType.key === type.key)
                )
              )
              .map((type) => {
                const currentRegionConfig = config.supported_options.regions.find(r => r.key === region);
                const isActiveForRegion = currentRegionConfig?.active_transportation_types?.some(activeType => activeType.key === type.key) ?? false;
                return (
                <button 
                  key={type.key}
                  className={`transport-option-button ${transportationType === type.key ? 'selected' : ''}`}
                  onClick={() => setTransportationType(type.key)}
                  title={type.display_name}
                  disabled={!isActiveForRegion}
                >
                  {type.key === 'Car' && <FaCar />}
                  {type.key === 'Bicycle' && <FaBicycle />}
                  {type.key === 'Walking' && <FaWalking />}
                </button>
              );
            })}
            <button className="info-button" onClick={() => setShowAddressInputInfo(true)}>
              <FaInfoCircle />
            </button>
            {showAddressInputInfo && (
              <div className="info-popup" ref={addressInputInfoRef}>
                <p>Please enter one address per line. You can enter between 2 and {config.max_addresses} addresses. Each address can be up to {config.max_address_length} characters long.</p>
                <p>The first address in your list will be considered the starting point of your route, and the last address will be the final destination.</p>
                <p>All addresses in between will be optimized for the most efficient route.</p>
                <p>Hint: For circular routes the last address/line should be the same as the first one.</p>
              </div>
            )}
          </div>
        </h3>
        <textarea
          placeholder={`Enter addresses, one per line (max ${config.max_addresses} addresses, each up to ${config.max_address_length} characters)`}
          rows="10"
          value={addresses}
          onChange={(e) => setAddresses(e.target.value)}
          maxLength={config.max_address_length * config.max_addresses + config.max_addresses - 1} // Rough estimate for total length including newlines
        ></textarea>
        <div className="button-group">
          <button onClick={handleOptimizeRoute}>Optimize Route</button>
          <button onClick={handleClear} className="button-secondary">Clear</button>
        </div>
        {error && <p className="error-message">Error: {error}</p>}
      </div>
      {renderAd('middle')}
      <div className="results-panel">
        {isLoading ? (
          <div className="spinner"></div>
        ) : routeData ? (
          <div className="results-section">
            <div className="route-summary card">
              <h3 className="panel-header">
                Optimized Route
                <div className="expand-collapse-buttons">
                  <button onClick={handleExpandAll} title="Expand All"><FaExpandArrowsAlt /></button>
                  <button onClick={handleCollapseAll} title="Collapse All"><FaCompressArrowsAlt /></button>
                </div>
              </h3>
              <ol>
                {routeData.optimized_route.map((stop, index) => {
                  const tooltipText = `Resolved: ${stop.fullAddress}\nLat: ${stop.lat}, Lon: ${stop.lon}`;
                  return (
                    <li key={index} onClick={() => handleToggleLeg(index)} className="expandable-item" title={tooltipText}>
                      <strong>{stop.address}</strong>
                      {index < routeData.optimized_route.length - 1 && (
                        <span className="expand-icon">{expandedLegs.has(index) ? '▲' : '▼'}</span>
                      )}
                      {expandedLegs.has(index) && index < routeData.legs.length && (
                        <div className="leg-details">
                          <p><em><FaInfoCircle /> {routeData.optimized_route[index].fullAddress}</em></p>
                          <p><em><FaMapMarkerAlt /> {routeData.optimized_route[index].lat}, {routeData.optimized_route[index].lon}</em></p>
                          <p><FaArrowRight /> <strong>{routeData.legs[index].to}</strong>: {getLocalizedDistance(routeData.legs[index].distance / 1000)}, {formatDuration(routeData.legs[index].duration)}</p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>

            <div className="route-totals card">
              <h3 className="panel-header">
                Totals
                <div className="distance-unit-options-header">
                  <button 
                    className={`distance-unit-button ${distanceUnit === 'km' ? 'selected' : ''}`}
                    onClick={() => setDistanceUnit('km')}
                    title="Kilometers"
                  >
                    km
                  </button>
                  <button 
                    className={`distance-unit-button ${distanceUnit === 'miles' ? 'selected' : ''}`}
                    onClick={() => setDistanceUnit('miles')}
                    title="Miles"
                  >
                    mi
                  </button>
                  <button className="info-button" onClick={() => setShowDistanceInfo(true)}>
                    <FaInfoCircle />
                  </button>
                  {showDistanceInfo && (
                    <div className="info-popup" ref={distanceInfoRef}>
                      <p>The total time and distance displayed are approximate and may vary from calculations by other navigation systems.</p>
                      <p>You can open the optimized route in various navigation applications using the "Open in..." button for more detailed information and real-time navigation.</p>
                    </div>
                  )}
                </div>
              </h3>
              <p><FaRoute /> <strong>Total distance:</strong> {getLocalizedDistance(routeData.total_distance / 1000)}</p>
              <p><FaClock /> <strong>Total duration:</strong> {formatDuration(routeData.total_duration)}</p>
            </div>

            <div className="button-group">
              <div className="open-in-dropdown" ref={dropdownRef}>
                <button onClick={() => setShowOpenInDropdown(!showOpenInDropdown)} className="dropdown-toggle">
                  Open in... <FaChevronDown />
                </button>
                {showOpenInDropdown && (
                  <div className="dropdown-menu">
                    <button onClick={openGoogleMaps}>Google Maps</button>
                    <button onClick={openGraphHopper}>GraphHopper</button>
                    <button onClick={openABetterRouteplanner}>ABR</button>
                    <button onClick={openBingMaps}>Bing Maps</button>
                  </div>
                )}
              </div>
              <div className="open-in-dropdown" ref={copyDropdownRef}> {/* Reusing class name for styling */} 
                <button onClick={() => setShowCopyDropdown(!showCopyDropdown)} className="dropdown-toggle">
                  Copy to Clipboard <FaChevronDown />
                </button>
                {showCopyDropdown && (
                  <div className="dropdown-menu">
                    <button onClick={handleCopyOriginal}>Original Addresses</button>
                    <button onClick={handleCopyResolved}>Resolved Addresses</button>
                    <button onClick={handleCopyCsv}>All Info (CSV)</button>
                  </div>
                )}
              </div>
              <div>
                {copySuccess && <span className="copy-success-message">{copySuccess}</span>}
              </div>
            </div>
          </div>
        ) : (
          <div className="initial-message">
            <h2>Your optimized route will appear here.</h2>
            <p>Enter your addresses on the left and click "Optimize Route" to begin.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
